import {
  AI_AUTOPILOT_USER_ID,
  CHATBOT_USER_ID,
} from "@redotech/ai-autopilot-constants/constant";
import { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import { getUserAvatarUrl } from "@redotech/redo-merchant-app-common/get-avatar-url";
import type {
  RedoListItem,
  TextRedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import ChatBotIcon from "@redotech/redo-web/icon-old/chat-bot.svg";

/**
 * Minimal user shape the assignee helpers need. `_id` may arrive as a raw
 * 24-hex ObjectId string (the legacy `team.users` / `GetUser` path used by the
 * bulk actions bar) or as a branded `MongoUserId` (the connected-team
 * `AssignableUser` collection used by the per-ticket selector). The helpers
 * normalize either form, so both callers share one implementation.
 */
export interface AssigneeListUser {
  _id: string;
  name: string;
  email: string;
}

export function getUserDisplayName(user: AssigneeListUser): string {
  const rawId = MongoUserId.toRawId(user._id);
  if (rawId === CHATBOT_USER_ID) {
    return "Chatbot";
  }
  if (rawId === AI_AUTOPILOT_USER_ID) {
    return "AI Autopilot";
  }
  return user.name;
}

export function getUserLeadingItem<T extends AssigneeListUser>(
  user: T,
  displayName: string,
): TextRedoListItem<T>["leadingItem"] {
  const rawId = MongoUserId.toRawId(user._id);
  if (rawId === CHATBOT_USER_ID) {
    return { type: "icon", Icon: ChatBotIcon };
  }
  if (rawId === AI_AUTOPILOT_USER_ID) {
    return { type: "icon", Icon: AiStarIcon };
  }
  return {
    type: "avatar",
    alt: "User profile picture",
    imageUrl: getUserAvatarUrl({ email: user.email }),
    name: displayName,
  };
}

export function buildAssigneeListItems<T extends AssigneeListUser>(
  users: T[],
): RedoListItem<T | null>[] {
  const userItems: RedoListItem<T>[] = users.map((user) => {
    const name = getUserDisplayName(user);
    return {
      type: "text",
      id: MongoUserId.fromString(MongoUserId.toRawId(user._id)),
      text: name,
      value: user,
      leadingItem: getUserLeadingItem(user, name),
    };
  });

  const unassigned: RedoListItem<null> = {
    type: "text",
    id: "unassigned",
    text: "Unassigned",
    value: null,
  };

  return [unassigned, ...userItems];
}
